import moment from 'moment-timezone'

const formatCity = location => {
	return `${location?.address?.city}, ${location?.address?.state}`
}

const formatTime = (date, timezone) => {
	if (!date) return ''
	return moment(date)
		.tz(timezone || 'America/New_York')
		.format('ddd MMM DD HH:mm z')
}

const validatePriceInputs = (adjustedPrice, adjustedPricePerMile) => {
	const price = parseFloat(adjustedPrice)
	const pricePerMile = parseFloat(adjustedPricePerMile)
	if (isNaN(price) || price <= 0) return 'Please enter a valid price'
	if (isNaN(pricePerMile) || pricePerMile <= 0)
		return 'Please enter a valid price per mile'
	if (price < 0) return 'Price cannot be negative'
	if (pricePerMile < 0) return 'Price per mile cannot be negative'
	if (price > 50000) return 'Price seems too high (max $50,000)'
	if (pricePerMile > 100) return 'Price per mile seems too high (max $100/mile)'
	return null
}

const transformLoadForExtension = (
	loadData,
	{
		adjustedPrice,
		adjustedPricePerMile,
		minMiles,
		maxMiles,
		stemTime,
		originRadius,
		destinationRadius,
		updatedPickupTime,
		updatedDeliveryTime,
	}
) => {
	const now = new Date()
	const originalPickupTime = new Date(
		loadData.pickup?.date_local || new Date().toISOString()
	)
	const originalDeliveryTime = new Date(
		loadData.dropOff?.date_local || new Date().toISOString()
	)

	let adjustedPickupTime
	let adjustedDeliveryTime
	const datesWereAdjusted = originalPickupTime.getTime() < now.getTime()

	if (datesWereAdjusted) {
		adjustedPickupTime = new Date(now.getTime() + 2 * 60 * 60 * 1000)

		const originalTransitTime =
			originalDeliveryTime.getTime() - originalPickupTime.getTime()
		const minTransitTime = 4 * 60 * 60 * 1000
		const transitTime = Math.max(originalTransitTime, minTransitTime)

		adjustedDeliveryTime = new Date(adjustedPickupTime.getTime() + transitTime)
	} else {
		adjustedPickupTime = originalPickupTime
		adjustedDeliveryTime = originalDeliveryTime
	}

	const transformedLoad = {
		id: loadData._id,

		startLocation: {
			city: loadData.pickup?.address?.city || '',
			state: loadData.pickup?.address?.state || '',
			latitude:
				loadData.pickup?.location?.coordinates?.[1] ||
				loadData.pickup?.address?.latitude ||
				0,
			longitude:
				loadData.pickup?.location?.coordinates?.[0] ||
				loadData.pickup?.address?.longitude ||
				0,
			timeZone: loadData.pickup?.timezone || 'America/New_York',
		},
		endLocation: {
			city: loadData.dropOff?.address?.city || '',
			state: loadData.dropOff?.address?.state || '',
			latitude:
				loadData.dropOff?.location?.coordinates?.[1] ||
				loadData.dropOff?.address?.latitude ||
				0,
			longitude:
				loadData.dropOff?.location?.coordinates?.[0] ||
				loadData.dropOff?.address?.longitude ||
				0,
			timeZone: loadData.dropOff?.timezone || 'America/New_York',
		},

		firstPickupTime: (() => {
			const date = new Date(updatedPickupTime || adjustedPickupTime)
			date.setSeconds(0, 0)
			return date.toISOString().replace(/\.\d{3}Z$/, 'Z')
		})(),
		lastDeliveryTime: (() => {
			const date = new Date(updatedDeliveryTime || adjustedDeliveryTime)
			date.setSeconds(0, 0)
			return date.toISOString().replace(/\.\d{3}Z$/, 'Z')
		})(),

		totalDistance: {
			value: loadData.distance || 0,
		},
		stopCount: loadData.stopCount || loadData.loads?.length || 1,
		transitOperatorType:
			loadData.driverType === 'Solo' ? 'SINGLE_DRIVER' : 'TEAM_DRIVER',
		workOpportunityType: 'ONE_WAY',

		payout: {
			value: parseFloat(adjustedPrice) || 0,
			unit: 'USD',
		},

		stops: (loadData.stops || []).map(stop => {
			if (!datesWereAdjusted) return stop

			const finalPickupTime = updatedPickupTime || adjustedPickupTime
			const finalDeliveryTime = updatedDeliveryTime || adjustedDeliveryTime
			const pickupTimeDiff =
				finalPickupTime.getTime() - originalPickupTime.getTime()
			const deliveryTimeDiff =
				finalDeliveryTime.getTime() - originalDeliveryTime.getTime()

			return {
				...stop,
				stops: (stop.stops || []).map((innerStop, index) => {
					const isFirstStop = index === 0
					const timeDiff = isFirstStop ? pickupTimeDiff : deliveryTimeDiff

					return {
						...innerStop,
						actions: (innerStop.actions || []).map(action => {
							const adjustedDate = new Date(
								new Date(action.plannedTime).getTime() + timeDiff
							)
							adjustedDate.setSeconds(0, 0)
							return {
								...action,
								plannedTime: adjustedDate
									.toISOString()
									.replace(/\.\d{3}Z$/, 'Z'),
							}
						}),
					}
				}),
			}
		}),

		loads: loadData.loads
			? loadData.loads.map(stop => {
					// Determine equipment type based on trailerTypes or equipmentType
					let equipmentType = 'FIFTY_THREE_FOOT_TRUCK'

					if (stop.equipmentType) {
						equipmentType = stop.equipmentType
					} else if (loadData.trailerTypes?.length > 0) {
						const trailerType = loadData.trailerTypes[0]
						if (trailerType.includes("53'")) {
							equipmentType = 'FIFTY_THREE_FOOT_TRUCK'
						} else if (trailerType.includes("40'")) {
							equipmentType = 'FORTY_FOOT_CONTAINER'
						} else if (trailerType.includes("26'")) {
							equipmentType = 'TWENTY_SIX_FOOT_BOX_TRUCK'
						}
					} else if (loadData.equipment === 'DRY VAN') {
						equipmentType = 'FIFTY_THREE_FOOT_TRUCK'
					}

					return {
						equipmentType: equipmentType,
						stops:
							stop.stops ||
							(loadData.trailerProvided === 'Provided'
								? [
										{
											trailerDetails: [
												{
													assetOwner: 'PROVIDED',
												},
											],
										},
								  ]
								: []),
					}
			  })
			: [
					{
						equipmentType: (() => {
							if (loadData.trailerTypes?.length > 0) {
								const trailerType = loadData.trailerTypes[0]
								if (trailerType.includes("53'")) return 'FIFTY_THREE_FOOT_TRUCK'
								if (trailerType.includes("40'")) return 'FORTY_FOOT_CONTAINER'
								if (trailerType.includes("26'"))
									return 'TWENTY_SIX_FOOT_BOX_TRUCK'
							}
							return loadData.equipment === 'DRY VAN'
								? 'FIFTY_THREE_FOOT_TRUCK'
								: 'FIFTY_THREE_FOOT_TRUCK'
						})(),
						stops:
							loadData.trailerProvided === 'Provided'
								? [
										{
											trailerDetails: [
												{
													assetOwner: 'PROVIDED',
												},
											],
										},
								  ]
								: [],
					},
			  ],

		datesWereAdjusted: datesWereAdjusted,

		minMiles: parseFloat(minMiles) || 0,
		maxMiles: parseFloat(maxMiles) || 0,
		pricePerMile: parseFloat(adjustedPricePerMile) || 0,
		payoutValue: parseFloat(adjustedPrice) || 0,
		stemTime: stemTime,
		originRadius: originRadius,
		destinationRadius: destinationRadius,
		startTime: (() => {
			const date = new Date(updatedPickupTime || adjustedPickupTime)
			date.setSeconds(0, 0)
			return date.toISOString().replace(/\.\d{3}Z$/, 'Z')
		})(),
		endTime: (() => {
			const date = new Date(updatedDeliveryTime || adjustedDeliveryTime)
			date.setSeconds(0, 0)
			return date.toISOString().replace(/\.\d{3}Z$/, 'Z')
		})(),
	}

	return transformedLoad
}

const getTrailerDetails = load => {
	const trailerTypes = new Set()
	let isPowerOnly = false

	load?.loads?.forEach(loadItem => {
		const trailerDetails = loadItem?.equipmentType

		if (trailerDetails) {
			if (
				trailerDetails === 'FIFTY_THREE_FOOT_TRUCK' ||
				trailerDetails === 'FIFTY_THREE_FOOT_A5_AIR_TRAILER'
			) {
				trailerTypes.add("53' Trailer")
			} else if (trailerDetails === 'FIFTY_THREE_FOOT_CONTAINER') {
				trailerTypes.add("53' Container")
			} else if (trailerDetails === 'FORTY_FOOT_CONTAINER') {
				trailerTypes.add("40' Container")
			} else if (trailerDetails === 'TWENTY_SIX_FOOT_BOX_TRUCK') {
				trailerTypes.add("26' Truck")
			} else {
				const trailerWords = trailerDetails.split('_')
				const formattedTrailerDetails = trailerWords
					.map(word => {
						if (word === 'FOOT') {
							return "'"
						} else {
							return word.charAt(0) + word.slice(1).toLowerCase()
						}
					})
					.join(' ')
				trailerTypes.add(formattedTrailerDetails)
			}
		}

		for (const stop of loadItem?.stops || []) {
			const stopTrailerDetails = stop?.trailerDetails

			if (stopTrailerDetails && stopTrailerDetails.length > 0) {
				for (const trailer of stopTrailerDetails) {
					if (trailer.assetOwner !== undefined && trailer.assetOwner !== null) {
						isPowerOnly = true
						break
					}
				}
			}

			if (isPowerOnly) break
		}
	})

	// Fallback to load.trailerTypes if no trailer types found in loads
	if (trailerTypes.size === 0 && load?.trailerTypes?.length > 0) {
		load.trailerTypes.forEach(type => {
			if (type.includes("53'")) {
				trailerTypes.add("53' Trailer")
			} else if (type.includes("40'")) {
				trailerTypes.add("40' Container")
			} else if (type.includes("26'")) {
				trailerTypes.add("26' Truck")
			} else {
				trailerTypes.add(type)
			}
		})
	}

	if (trailerTypes.size === 0) {
		if (load?.equipment === 'DRY VAN') {
			trailerTypes.add("53' Trailer")
		} else {
			trailerTypes.add("53' Trailer")
		}
	}

	const trailerTypeString = Array.from(trailerTypes).join(', ')
	const loadType =
		isPowerOnly || load?.trailerProvided === 'Provided'
			? 'Provided'
			: 'Required'

	return `${trailerTypeString} (${loadType})`
}

export {
	transformLoadForExtension,
	formatCity,
	formatTime,
	validatePriceInputs,
	getTrailerDetails,
}
